import React from 'react';

const PrintableEarnings = ({ earnings, totalEarnings, paymentHistory, userDetails }) => {
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper function to get payment status with color
  const getPaymentStatus = (payment) => {
    if (payment.amountUnpaid === 0) {
      return <span className="font-medium text-green-600">Paid</span>;
    }
    if (payment.amountPaid > 0) {
      return <span className="font-medium text-yellow-600">Partial</span>;
    }
    return <span className="font-medium text-red-600">Unpaid</span>;
  };

  return (
    <div className="bg-white p-8">
      {/* Header with Logo and Title */}
      <div className="mb-8 flex items-start justify-between border-b border-gray-200 pb-4">
        <div className="flex items-center">
          <div className="text-2xl font-bold text-orange-500">INFUBER</div>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Earnings Report</h1>
          <p className="text-sm text-gray-500">Generated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>

      {/* User Information Section */}
      <div className="mb-8 rounded-2xl border border-gray-200 bg-gray-50 p-6">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Affiliate Information</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-sm text-gray-500">Name</p>
              <p className="font-medium text-gray-900">
                {userDetails?.firstName && userDetails?.lastName 
                  ? `${userDetails.firstName} ${userDetails.lastName}`
                  : userDetails?.username || 'N/A'}
              </p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{userDetails?.email || 'N/A'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-sm text-gray-500">Mobile Number</p>
              <p className="font-medium text-gray-900">{userDetails?.mobile || 'N/A'}</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-gray-500">Location</p>
              <p className="font-medium text-gray-900">
                {userDetails?.country && userDetails?.state && userDetails?.city && userDetails?.postalCode 
                  ? `${userDetails.country} - ${userDetails.state} - ${userDetails.city} - ${userDetails.postalCode}`
                  : userDetails?.country || userDetails?.state || userDetails?.city || userDetails?.postalCode || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Earnings Summary</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-sm text-gray-500">Total Due</p>
            <p className="text-lg font-medium text-gray-900">
              {formatCurrency(totalEarnings.totalDue)}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-sm text-gray-500">Amount Paid</p>
            <p className="text-lg font-medium text-green-600">
              {formatCurrency(totalEarnings.amountPaid)}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-sm text-gray-500">Amount Unpaid</p>
            <p className="text-lg font-medium text-red-600">
              {formatCurrency(totalEarnings.amountUnpaid)}
            </p>
          </div>
        </div>
      </div>

      {/* Current Earnings Table */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Current Earnings by Campaign</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-lg border border-gray-200" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Brand</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Campaign</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Due</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount Paid</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount Unpaid</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Updated</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {earnings.map((earning, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{earning.brandName}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{earning.campaignName}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                    {formatCurrency(earning.totalDue)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-green-600">
                    {formatCurrency(earning.amountPaid)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-red-600">
                    {formatCurrency(earning.amountUnpaid)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                    {new Date(earning.lastUpdated).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm">
                    {getPaymentStatus(earning)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment History Table */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Payment History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-lg border border-gray-200" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Brand</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Campaign</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Due</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount Paid</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount Unpaid</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Updated By</th>
                <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {paymentHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-4 text-center text-gray-500">
                    No payment history available.
                  </td>
                </tr>
              ) : (
                paymentHistory.map((payment, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                      {new Date(payment.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{payment.brandName}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{payment.campaignName}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                      {formatCurrency(payment.totalDue)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-green-600">
                      {formatCurrency(payment.amountPaid)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-red-600">
                      {formatCurrency(payment.amountUnpaid)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                      {payment.updatedBy?.name || payment.brandName || 'System'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm">
                      {getPaymentStatus(payment)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Infuber. All rights reserved.</p>
          </div>
          <div className="text-sm text-gray-500">
            <p>This is a computer-generated document.</p>
            <p>Document ID: {Math.random().toString(36).substring(2, 15)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableEarnings;
